import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider
} from '@mui/material';
import {
    Whatshot as BurstIcon,
    AccessTime as TimeIcon,
    AttachMoney as MoneyIcon,
    TrendingUp as TrendingIcon
} from '@mui/icons-material';

interface BurstData {
    inActiveBurst: boolean;
    transactionTimes: number[];
    transactionAmounts: number[];
    startTime: number;
    endTime: number;
    totalVolumeUSD: number;
    isComplete: boolean;
}

interface BurstDataDisplayProps {
    data: BurstData;
    compact?: boolean;
}

const BurstDataDisplay: React.FC<BurstDataDisplayProps> = ({ data, compact = false }) => {
    const {
        inActiveBurst,
        transactionTimes,
        transactionAmounts,
        startTime,
        endTime,
        totalVolumeUSD,
        isComplete
    } = data;

    // If no active burst, show simple message
    if (!inActiveBurst && transactionTimes.length === 0) {
        return (
            <Alert severity="info" icon={<BurstIcon />}>
                No active burst detected for this wallet
            </Alert>
        );
    }

    // Format time helper
    const formatTime = (timestamp: number) => {
        if (timestamp === 0) return 'N/A';
        const date = new Date(timestamp * 1000);
        return compact ? date.toLocaleTimeString() : date.toLocaleString();
    };

    // Format USD helper - values are already in decimal USD format from parser
    const formatUSD = (amountUSD: number) => {
        return `$${amountUSD.toFixed(2)}`;
    };

    // Calculate time span
    const timeSpan = transactionTimes.length > 0
        ? transactionTimes[transactionTimes.length - 1] - transactionTimes[0]
        : 0;

    return (
        <Paper
            elevation={3}
            sx={{
                p: 3,
                bgcolor: inActiveBurst ? 'rgba(255, 87, 34, 0.05)' : 'background.paper',
                border: inActiveBurst ? '2px solid rgba(255, 87, 34, 0.3)' : 'none'
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BurstIcon color={inActiveBurst ? 'error' : 'action'} />
                    <Typography variant="h6">
                        {inActiveBurst ? '🔥 Active Burst Detected' : 'Burst Data'}
                    </Typography>
                </Box>
                <Chip
                    label={isComplete ? 'Completed' : inActiveBurst ? 'Active' : 'Inactive'}
                    color={inActiveBurst ? 'error' : 'default'}
                    size="small"
                />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Summary Stats */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <TrendingIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                            Transactions
                        </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight="bold">
                        {transactionTimes.length}
                    </Typography>
                </Box>

                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <MoneyIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                            Total Volume
                        </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                        {formatUSD(totalVolumeUSD)}
                    </Typography>
                </Box>

                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                            Time Span
                        </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight="bold">
                        {timeSpan}s
                    </Typography>
                </Box>
            </Box>

            {/* Burst Period */}
            {startTime > 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Started:</strong> {formatTime(startTime)}
                    </Typography>
                    {endTime > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Ended:</strong> {formatTime(endTime)}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Transaction Details Table */}
            {transactionTimes.length > 0 && !compact && (
                <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Transaction Details:
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Time</TableCell>
                                    <TableCell align="right">Amount (USD)</TableCell>
                                    <TableCell align="right">Cumulative</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactionTimes.map((time, idx) => {
                                    const cumulativeVolume = transactionAmounts
                                        .slice(0, idx + 1)
                                        .reduce((sum, amt) => sum + amt, 0);

                                    return (
                                        <TableRow key={idx}>
                                            <TableCell>{idx + 1}</TableCell>
                                            <TableCell>{formatTime(time)}</TableCell>
                                            <TableCell align="right">
                                                {formatUSD(transactionAmounts[idx] || 0)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>{formatUSD(cumulativeVolume)}</strong>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {/* Compact View */}
            {compact && transactionTimes.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        First: {formatTime(transactionTimes[0])} |
                        Last: {formatTime(transactionTimes[transactionTimes.length - 1])}
                    </Typography>
                </Box>
            )}

            {/* Warning for high-frequency bursts */}
            {transactionTimes.length >= 5 && inActiveBurst && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>High-Frequency Trading Detected!</strong><br />
                    This wallet has executed {transactionTimes.length} transactions in rapid succession.
                    This may indicate bot activity or manipulation attempts.
                </Alert>
            )}
        </Paper>
    );
};

export default BurstDataDisplay;
